import { Link } from "react-router-dom";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";

const EFFECTIVE_DATE = "June 10, 2026";

export default function PrivacyPolicyPage() {
  return (
    <>
      <NavBar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <Link to="/" className="text-sm text-gg-muted hover:text-gg-green">← Back home</Link>

        <header className="mt-6">
          <p className="uppercase tracking-[0.22em] text-xs text-gg-brown font-semibold">Legal</p>
          <h1 className="font-heading text-4xl sm:text-5xl text-[var(--gg-text)] mt-3 leading-tight">
            Privacy Policy
          </h1>
          <p className="text-sm text-gg-muted mt-3">Effective {EFFECTIVE_DATE}</p>
        </header>

        <article className="mt-10 space-y-7 text-[15px] leading-relaxed text-[var(--gg-text)]/95">
          <Section title="Who we are">
            <p>
              Graceful Garden Coaching &amp; Design is an Arizona homestead-education
              business owned and operated by Heather Mich, located at 3361 N Reed Rd,
              Chino Valley, AZ 86323. We can be reached at (805) 444-6144 or
              <a className="text-gg-green hover:underline mx-1" href="mailto:Heather@gracefulgardencoaching.com">
                Heather@gracefulgardencoaching.com
              </a>.
            </p>
          </Section>

          <Section title="What we collect">
            <p>When you register a student or request a 1-on-1 session, we collect:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Student first and last name</li>
              <li>Parent / guardian name, email address, and phone number</li>
              <li>Arizona ESA / ClassWallet contract number</li>
              <li>Whether a parent is participating in the cooking-class portion</li>
              <li>Your typed digital signature on the homestead liability waiver</li>
              <li>Optional notes you provide (e.g., a referring family's first name)</li>
            </ul>
            <p className="mt-3">
              We do <strong>not</strong> store credit-card numbers. Card payments are
              processed by Stripe; we only receive a payment status flag.
            </p>
          </Section>

          <Section title="Why we collect it">
            <p>We use this information only to:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Generate a ClassWallet-compatible PDF invoice in your child's name</li>
              <li>Email registration confirmations, day-3 reminders, and class-day instructions</li>
              <li>Coordinate workshop logistics (bulk ingredient counts, name tags, attendance)</li>
              <li>Comply with the homestead waiver and any documentation an ESA reviewer may request</li>
            </ul>
            <p className="mt-3">
              We never sell, rent, or trade your information.
            </p>
          </Section>

          <Section title="Who we share it with">
            <p>Limited, purpose-bound third-party processors only:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li><strong>Stripe</strong> — credit-card processing for parent add-on fees</li>
              <li><strong>Resend</strong> — delivery of transactional emails on our verified domain</li>
              <li><strong>Google Calendar</strong> — calendar event creation when a 1-on-1 is confirmed (parent receives an event invite at the email you provided)</li>
              <li>The State of Arizona / ClassWallet — only the data <em>you</em> upload when submitting the invoice we generate</li>
            </ul>
            <p className="mt-3">
              We will share information when required by a lawful subpoena, court order,
              or to prevent imminent harm.
            </p>
          </Section>

          <Section title="How we protect it">
            <ul className="list-disc list-inside space-y-1">
              <li>All traffic uses HTTPS / TLS encryption</li>
              <li>Admin access is locked to a single Google account via Google Sign-In; there is no password to crack</li>
              <li>PDF invoice URLs are cryptographically signed so they cannot be guessed</li>
              <li>Database access is restricted to the backend service and is not exposed to the public internet</li>
              <li>Every admin action against your record is recorded in an audit log</li>
            </ul>
          </Section>

          <Section title="How long we keep it">
            <p>
              Registration records are retained for <strong>three (3) years</strong> after the
              class date, matching standard ESA / ClassWallet audit windows. After that we
              delete personally identifying information and keep only aggregate, non-identifying
              statistics.
            </p>
          </Section>

          <Section title="Children's privacy">
            <p>
              The information we collect about a registered student is provided by the
              parent or legal guardian. We do not knowingly collect any personal information
              directly from a child under the age of 13.
            </p>
          </Section>

          <Section title="Your rights">
            <p>You may, at any time, request that we:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Send you a copy of every record we hold about you and your student(s)</li>
              <li>Correct any inaccurate field</li>
              <li>Delete all records (unless retention is required to substantiate a ClassWallet submission already in process)</li>
            </ul>
            <p className="mt-3">
              Email Heather directly with your request. We respond within seven (7) business days.
            </p>
          </Section>

          <Section title="Changes to this policy">
            <p>
              If we make material changes, we'll update the Effective Date above and email
              every active family at least 14 days before the change takes effect.
            </p>
          </Section>
        </article>

        <p className="mt-12 text-xs text-gg-muted">
          See also: <Link className="text-gg-green hover:underline" to="/terms">Terms &amp; Refund Policy</Link>.
        </p>
      </main>
      <Footer />
    </>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-heading text-2xl text-gg-brown leading-snug mb-3">{title}</h2>
      <div className="space-y-2">{children}</div>
    </section>
  );
}
